import { getHeaders } from "./api";
import { ClientBrainBuilderMessage, ClientBrainDocument, ClientBrainInstance, ClientBrainTemplate } from "../components/ClientBrain/clientBrainTypes";
import { normalizeDocument, normalizeInstance, normalizeTemplate } from "../components/ClientBrain/clientBrainMutations";

const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

export class ClientBrainConflictError extends Error {
    document: ClientBrainDocument;

    constructor(message: string, document: ClientBrainDocument) {
        super(message);
        this.name = "ClientBrainConflictError";
        this.document = document;
    }
}

export class ClientBrainInstanceConflictError extends Error {
    constructor() { super("Client Brain changed while you were editing. Reload the latest values before making a new edit."); this.name = "ClientBrainInstanceConflictError"; }
}

export type ClientBrainAiSource = "document" | "inferred";

export interface ClientBrainUnfilledItem {
    field_id?: string;
    section_id?: string;
    label: string;
    reason: string;
}

export interface ClientBrainGenerationMeta {
    sources: {
        fields: Record<string, ClientBrainAiSource>;
        goals: Record<string, ClientBrainAiSource>;
    };
    unfilled: ClientBrainUnfilledItem[];
}

export class ClientBrainGenerateError extends Error {
    kind: "unreadable_files" | "timeout" | "too_large" | "request";
    files: string[];

    constructor(kind: ClientBrainGenerateError["kind"], message: string, files: string[] = []) {
        super(message);
        this.name = "ClientBrainGenerateError";
        this.kind = kind;
        this.files = files;
    }
}

async function throwForStatus(response: Response): Promise<never> {
    let message = `Request failed with status ${response.status}`;
    try {
        const data = await response.json();
        if (data?.error) message = data.error;
    } catch {
        /* fall through */
    }
    throw new Error(message);
}

async function throwConflict(response: Response, fallbackMessage: string): Promise<never> {
    let message = fallbackMessage;
    let current: unknown = undefined;
    try {
        const data = await response.json();
        if (data?.error) message = data.error;
        current = data?.client_brain;
    } catch {
        /* fall through */
    }
    throw new ClientBrainConflictError(message, normalizeDocument(current));
}

export async function getCompanyClientBrainApi(idToken: string | undefined): Promise<ClientBrainDocument> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/client-brain`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return normalizeDocument(data.client_brain);
}

export async function updateCompanyClientBrainApi(
    draft: ClientBrainTemplate | null,
    updatedAt: number | null,
    idToken: string | undefined
): Promise<ClientBrainDocument> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/client-brain`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ draft, updated_at: updatedAt }),
        credentials: "same-origin"
    });
    if (response.status === 409) {
        return throwConflict(response, "Client brain was updated by someone else");
    }
    if (!response.ok) {
        return throwForStatus(response);
    }
    const data = await response.json();
    return normalizeDocument(data.client_brain);
}

export async function publishCompanyClientBrainApi(updatedAt: number | null, idToken: string | undefined): Promise<ClientBrainDocument> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/client-brain/publish`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ updated_at: updatedAt }),
        credentials: "same-origin"
    });
    if (response.status === 409) {
        return throwConflict(response, "Client brain was updated by someone else");
    }
    if (!response.ok) {
        return throwForStatus(response);
    }
    const data = await response.json();
    return normalizeDocument(data.client_brain);
}

export async function buildClientBrainProposalApi(
    messages: ClientBrainBuilderMessage[],
    draft: ClientBrainTemplate | null,
    files: File[],
    idToken: string | undefined
): Promise<{ template: ClientBrainTemplate; skippedFiles: string[] }> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("payload", JSON.stringify({ messages, draft }));
    for (const file of files) {
        formData.append("files", file);
    }

    // The builder runs two reasoning passes over the uploads, so allow minutes — but never
    // hang forever. Held just under the Container Apps ingress idle timeout (240s, raisable
    // only with premium ingress) so this message wins over an opaque proxy disconnect.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 230000);
    let response: Response;
    try {
        response = await fetch(`${BACKEND_URI}/my-company/client-brain/builder`, {
            method: "POST",
            headers: { ...headers },
            body: formData,
            credentials: "same-origin",
            signal: controller.signal
        });
    } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
            throw new Error("The proposal timed out. Try again with fewer or smaller files.");
        }
        throw err;
    } finally {
        clearTimeout(timer);
    }
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        let skipped: string[] = [];
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
            if (Array.isArray(data?.skipped_files)) {
                skipped = data.skipped_files.filter((name: unknown): name is string => typeof name === "string" && name.length > 0);
            }
        } catch {
            /* fall through */
        }
        if (skipped.length > 0) {
            message = `${message} (could not read: ${skipped.join(", ")})`;
        }
        throw new Error(message);
    }
    const data = await response.json();
    const template = normalizeTemplate(data.template);
    if (!template) {
        throw new Error("The assistant did not return a template");
    }
    const skippedFiles = Array.isArray(data.skipped_files) ? data.skipped_files.filter((name: unknown) => typeof name === "string") : [];
    return { template, skippedFiles };
}

function normalizeSourceMap(data: unknown): Record<string, ClientBrainAiSource> {
    if (!data || typeof data !== "object") {
        return {};
    }
    const sources: Record<string, ClientBrainAiSource> = {};
    for (const [id, source] of Object.entries(data as Record<string, unknown>)) {
        if (source === "document" || source === "inferred") {
            sources[id] = source;
        }
    }
    return sources;
}

function normalizeUnfilled(data: unknown): ClientBrainUnfilledItem[] {
    if (!Array.isArray(data)) {
        return [];
    }
    return data.flatMap(item => {
        if (!item || typeof item !== "object") {
            return [];
        }
        const raw = item as Record<string, unknown>;
        const fieldId = typeof raw.field_id === "string" ? raw.field_id : undefined;
        const sectionId = typeof raw.section_id === "string" ? raw.section_id : undefined;
        if ((!fieldId && !sectionId) || typeof raw.label !== "string" || typeof raw.reason !== "string") {
            return [];
        }
        return [{ field_id: fieldId, section_id: sectionId, label: raw.label, reason: raw.reason }];
    });
}

export async function generateProjectClientBrainApi(
    projectId: string,
    prompt: string,
    files: File[],
    idToken: string | undefined
): Promise<{ instance: ClientBrainInstance } & ClientBrainGenerationMeta> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("payload", JSON.stringify({ prompt }));
    for (const file of files) {
        formData.append("files", file);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 230000);
    let response: Response;
    try {
        response = await fetch(`${BACKEND_URI}/projects/${projectId}/client-brain/generate`, {
            method: "POST",
            headers: { ...headers },
            body: formData,
            credentials: "same-origin",
            signal: controller.signal
        });
    } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
            throw new ClientBrainGenerateError("timeout", "The profile generation timed out");
        }
        throw err;
    } finally {
        clearTimeout(timer);
    }

    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        let errorCode = "";
        let unreadableFiles: string[] = [];
        try {
            const data = await response.json();
            if (typeof data?.error === "string") {
                message = data.error;
                errorCode = data.error;
            }
            if (Array.isArray(data?.files)) {
                unreadableFiles = data.files.filter((name: unknown): name is string => typeof name === "string" && name.length > 0);
            }
        } catch {
            /* fall through */
        }
        if (errorCode === "unreadable_files") {
            throw new ClientBrainGenerateError("unreadable_files", message, unreadableFiles);
        }
        if (errorCode === "files_too_large" || response.status === 413) {
            throw new ClientBrainGenerateError("too_large", message);
        }
        throw new ClientBrainGenerateError("request", message);
    }

    const data = await response.json();
    return {
        instance: normalizeInstance(data.instance),
        sources: {
            fields: normalizeSourceMap(data.sources?.fields),
            goals: normalizeSourceMap(data.sources?.goals)
        },
        unfilled: normalizeUnfilled(data.unfilled)
    };
}

const CLIENT_BRAIN_CACHE_PREFIX = "clientBrain:";

/** Remove all cached Client Brain templates (called on logout). */
export function clearClientBrainCache(): void {
    try {
        const stale: string[] = [];
        for (let index = 0; index < localStorage.length; index++) {
            const key = localStorage.key(index);
            if (key && key.startsWith(CLIENT_BRAIN_CACHE_PREFIX)) {
                stale.push(key);
            }
        }
        stale.forEach(key => localStorage.removeItem(key));
    } catch {
        // Storage unavailable — nothing to clear.
    }
}

export async function getProjectClientBrainApi(
    projectId: string,
    idToken: string | undefined
): Promise<{ template: ClientBrainTemplate | null; instance: ClientBrainInstance | null }> {
    const headers = await getHeaders(idToken);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    let response: Response;
    try {
        response = await fetch(`${BACKEND_URI}/projects/${projectId}/client-brain`, {
            method: "GET",
            headers: { ...headers },
            credentials: "same-origin",
            signal: controller.signal
        });
    } finally {
        clearTimeout(timer);
    }
    if (!response.ok) {
        return throwForStatus(response);
    }
    const data = await response.json();
    return {
        template: normalizeTemplate(data.template),
        instance: data.instance ? normalizeInstance({ ...data.instance, version: data.version }) : null
    };
}

export interface ClientBrainHistoryEntry {
    version: number;
    timestamp: number;
    edited_by_name: string;
    summary: string;
}

export async function getProjectClientBrainHistoryApi(projectId: string, idToken: string | undefined): Promise<ClientBrainHistoryEntry[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/client-brain/history`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    if (!response.ok) {
        return throwForStatus(response);
    }
    const data = await response.json();
    if (!Array.isArray(data.history)) {
        return [];
    }
    return data.history.filter(
        (entry: unknown): entry is ClientBrainHistoryEntry =>
            !!entry && typeof entry === "object" && typeof (entry as ClientBrainHistoryEntry).summary === "string"
    );
}

export async function updateProjectClientBrainApi(projectId: string, instance: ClientBrainInstance, idToken: string | undefined): Promise<ClientBrainInstance> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/client-brain`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ instance: Object.fromEntries(Object.entries(instance).filter(([key]) => key !== "version")), expected_version: instance.version ?? 0 }),
        credentials: "same-origin"
    });
    if (response.status === 409) throw new ClientBrainInstanceConflictError();
    if (!response.ok) {
        return throwForStatus(response);
    }
    const data = await response.json();
    return normalizeInstance({ ...data.instance, version: data.version });
}
